// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Tooltip,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Table,
  TableRow,
  TableCell,
  Divider,
  DialogTitle,
  Chip,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
// import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import AppConfig from "appConfig";
import DataTable from "theme/Tables/DataTable";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";
import { useAwaitDialog } from "app/components/status_dialog";

function CallsPage() {
  const pageTitle = "Call Logs";
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const statusDialog = useAwaitDialog();

  const [pageLoaded, setPageLoaded] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);
  const [calls, setCalls] = useState([]);
  const [call, setCall] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [transcript, setTranscript] = useState(null);

  const tableColumns = [
    { Header: "#", accessor: "direction_icon", align: "left" },
    { Header: "Direction", accessor: "direction", align: "left" },
    { Header: "Status", accessor: "status", align: "left" },
    { Header: "Agent", accessor: "agent", align: "left" },
    { Header: "Contact", accessor: "caller", align: "left" },
    { Header: "Duration", accessor: "duration", align: "left" },
    { Header: "Cost", accessor: "charge", align: "left" },
    { Header: "Outcome", accessor: "outcome", align: "left" },
    { Header: "Recording", accessor: "recording", align: "center" },
    { Header: "GPS", accessor: "location", align: "center" },
    { Header: "Date", accessor: "date", align: "right" },
  ];

  const buildRateRows = (data, crcy) => {
    function getCallIcon(row) {
      if (row.direction === "inbound") {
        if (row.status === "completed") {
          return "phone_callback";
        } else {
          return "phone_missed";
        }
      } else {
        if (row.status === "completed") {
          return "phone_forwarded";
        } else {
          return "call_end";
        }
      }
    }

    function getCallColor(row) {
      if (row.direction === "inbound") {
        if (row.status === "completed") {
          return "warning";
        } else {
          return "error";
        }
      } else {
        if (row.status === "completed") {
          return "success";
        } else {
          return "dark";
        }
      }
    }
    setCurrency(crcy);
    const rows = [];
    data.map((row) => {
      rows.push({
        direction_icon: (
          <MDButton
            size="small"
            iconOnly
            variant="outlined"
            color={getCallColor(row)}
            onClick={() => {
              if (row.status !== "completed") {
                return;
              }

              getCall(row.reference);
            }}
          >
            <Icon>{getCallIcon(row)}</Icon>
          </MDButton>
        ),
        direction: row.direction.toUpperCase(),
        status: row.status.toUpperCase(),
        agent: row.agent,
        caller: row.caller ?? "---",
        duration: `${row.duration}s`,
        charge: `${crcy}${(row.charge / 100).toLocaleString("en", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
        outcome: row.outcome,
        location: row.latitude ? (
          <a
            rel="noreferrer"
            target="_blank"
            href={`https://maps.google.com?q=${row.latitude},${row.longitude}`}
          >
            <Icon>location_on</Icon>
          </a>
        ) : (
          "---"
        ),
        recording: row.recording ? (
          <MDBox mt={1} variant="button">
            <a href={`${AppConfig.api_media_url}${row.recording}`} target="_blank" rel="noreferrer">
              <Icon>play_circle</Icon>
            </a>
          </MDBox>
        ) : (
          "---"
        ),
        date: row.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`/portal/voice/call_centers/${ref}/calls.json`)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            const rows = buildRateRows(result.data.data.calls, result.data.currency);
            setCalls(rows);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function getCall(callRef) {
    setShowLoading(true);
    await axios
      .get(`portal/voice/call_centers/${callRef}/call.json`)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setCall(result.data.data.call);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function generateAnalysis(callRef) {
    setCall(null);
    setShowLoading(true);
    await axios
      .get(`portal/voice/call_centers/${callRef}/analyse.json`)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success", "Success!");
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {transcript && (
        <Dialog open fullWidth maxWidth="md">
          <DialogTitle>Call Transcript</DialogTitle>
          <DialogContent>
            <MDBox
              sx={{
                maxHeight: 500,
                overflowY: "auto",
                px: 1,
                py: 2,
                bgcolor: "#f5f5f5",
                borderRadius: 2,
              }}
            >
              {call.analysis.transcript.map((item, index) => {
                const isAgent = item.speaker === "AGENT";

                return (
                  <MDBox
                    key={index}
                    display="flex"
                    justifyContent={isAgent ? "flex-start" : "flex-end"}
                    mb={2}
                  >
                    <MDBox
                      sx={{
                        maxWidth: "75%",
                        px: 2,
                        py: 1.5,
                        borderRadius: 3,
                        bgcolor: isAgent ? "dark.main" : "#ffffff",
                        color: isAgent ? "#fff" : "#000",
                        boxShadow: 1,
                      }}
                    >
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={0.5}
                      >
                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          color={isAgent ? "white" : "dark"}
                        >
                          {item.speaker}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          sx={{
                            opacity: 0.7,
                            ml: 2,
                          }}
                          color={isAgent ? "white" : "text"}
                        >
                          {Math.floor(item.timestamp_start)}s
                        </MDTypography>
                      </MDBox>
                      <MDTypography variant="body2" color={isAgent ? "white" : "dark"}>
                        {item.text}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                );
              })}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton variant="outlined" color="dark" onClick={() => setTranscript(null)}>
              x close
            </MDButton>
          </DialogActions>
        </Dialog>
      )}
      {call && (
        <Dialog open={call ? true : false} fullWidth maxWidth="lg">
          <DialogContent>
            <MDBox>
              <MDBox align="center">
                <MDTypography fontSize={36}>
                  <Icon color={call.direction === "inbound" ? "warning" : "success"}>
                    {call.direction === "inbound" ? "phone_callback" : "phone_forwarded"}
                  </Icon>
                </MDTypography>
                <MDTypography variant="caption" fontSize={14}>
                  {call.direction.toUpperCase()}
                </MDTypography>
                <Divider />
              </MDBox>
              <Grid mt={2} container spacing={3}>
                <Grid item xs={12} md={6} lg={6}>
                  <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <MDBox display="flex" alignItems="center" justifyContent="start" gap={1}>
                      <Icon
                        sx={{
                          color: "info",
                          fontSize: 24,
                        }}
                      >
                        info
                      </Icon>

                      <MDTypography variant="h6" fontWeight="bold">
                        Call Details
                      </MDTypography>
                    </MDBox>
                    <Chip
                      label={call.outcome ?? "---"}
                      sx={{
                        backgroundColor: "info.main",
                        color: "#fff",
                        fontWeight: "regular",
                      }}
                    />
                  </MDBox>
                  <MDBox mt={3}>
                    <Table>
                      <TableRow>
                        <TableCell>
                          <MDTypography variant="h6" fontWeight="regular">
                            Contact
                          </MDTypography>
                        </TableCell>
                        <TableCell align="right">
                          <MDTypography variant="body2" fontSize={14}>
                            {call.caller}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <MDTypography variant="h6" fontWeight="regular">
                            Agent
                          </MDTypography>
                        </TableCell>
                        <TableCell align="right">
                          <MDTypography variant="body2" fontSize={14}>
                            {call.agent}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <MDTypography variant="h6" fontWeight="regular">
                            Duration
                          </MDTypography>
                        </TableCell>
                        <TableCell align="right">
                          <MDTypography variant="body2" fontSize={14}>
                            {`${call.duration}s`}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <MDTypography variant="h6" fontWeight="regular">
                            Cost
                          </MDTypography>
                        </TableCell>
                        <TableCell align="right">
                          <MDTypography variant="body2" fontSize={14}>
                            {`${currency}${(call.charge / 100).toLocaleString("en", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <MDTypography variant="h6" fontWeight="regular">
                            GPS
                          </MDTypography>
                        </TableCell>
                        <TableCell align="right">
                          <MDTypography variant="body2" fontSize={14}>
                            <a
                              rel="noreferrer"
                              target="_blank"
                              href={`https://maps.google.com?q=${call.latitude},${call.longitude}`}
                            >
                              <Icon>location_on</Icon>
                            </a>
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <MDTypography variant="h6" fontWeight="regular">
                            Time
                          </MDTypography>
                        </TableCell>
                        <TableCell align="right">
                          <MDTypography variant="body2" fontSize={14}>
                            {call.created}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                    </Table>
                  </MDBox>
                  <MDBox mt={5} align="center">
                    {call.recording && (
                      <MDBox>
                        <MDTypography variant="h6">Call Recording</MDTypography>
                        <MDBox mt={3}>
                          <audio src={`${AppConfig.api_media_url}${call.recording}`} controls>
                            Your browser does not support the audio element.
                          </audio>
                        </MDBox>
                      </MDBox>
                    )}
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                  {call.analysis ? (
                    <MDBox>
                      <MDBox>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={1}
                        >
                          <MDBox display="flex" alignItems="center" justifyContent="start" gap={1}>
                            <Icon
                              sx={{
                                color: "#b455ff",
                                fontSize: 24,
                              }}
                            >
                              auto_awesome
                            </Icon>

                            <MDTypography variant="h6" fontWeight="bold">
                              AI Call Analysis
                            </MDTypography>
                          </MDBox>

                          <Chip
                            label="AI-Assist"
                            sx={{
                              backgroundColor: "primary.main",
                              color: "#fff",
                              fontWeight: "regular",
                            }}
                          />
                        </MDBox>

                        <MDBox
                          p={1}
                          borderRadius={3}
                          textAlign="center"
                          // sx={{
                          //   backgroundColor:
                          //     call.analysis.sentiment.sentiment === "angry"
                          //       ? "#fee2e2"
                          //       : call.analysis.sentiment.sentiment === "frustrated"
                          //       ? "#ffedd5"
                          //       : call.analysis.sentiment.sentiment === "confused"
                          //       ? "#fef9c3"
                          //       : call.analysis.sentiment.sentiment === "neutral"
                          //       ? "#f3f4f6"
                          //       : call.analysis.sentiment.sentiment === "satisfied"
                          //       ? "#dcfce7"
                          //       : "#dcfce7",
                          // }}
                        >
                          <MDBox
                            width={30}
                            height={30}
                            mx="auto"
                            mb={1}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            borderRadius="50%"
                            sx={{
                              backgroundColor:
                                call.analysis.sentiment.sentiment === "angry"
                                  ? "#ef4444"
                                  : call.analysis.sentiment.sentiment === "frustrated"
                                  ? "#f97316"
                                  : call.analysis.sentiment.sentiment === "confused"
                                  ? "#eab308"
                                  : call.analysis.sentiment.sentiment === "neutral"
                                  ? "#9ca3af"
                                  : call.analysis.sentiment.sentiment === "satisfied"
                                  ? "#22c55e"
                                  : "#16a34a",
                              color: "#fff",
                            }}
                          >
                            <Icon>
                              {call.analysis.sentiment.sentiment === "angry"
                                ? "sentiment_very_dissatisfied"
                                : call.analysis.sentiment.sentiment === "frustrated"
                                ? "mood_bad"
                                : call.analysis.sentiment.sentiment === "confused"
                                ? "sentiment_neutral"
                                : call.analysis.sentiment.sentiment === "neutral"
                                ? "sentiment_neutral"
                                : call.analysis.sentiment.sentiment === "satisfied"
                                ? "sentiment_satisfied"
                                : "sentiment_very_satisfied"}
                            </Icon>
                          </MDBox>

                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            fontSize={16}
                            sx={{
                              color:
                                call.analysis.sentiment.sentiment === "angry"
                                  ? "#b91c1c"
                                  : call.analysis.sentiment.sentiment === "frustrated"
                                  ? "#c2410c"
                                  : call.analysis.sentiment.sentiment === "confused"
                                  ? "#a16207"
                                  : call.analysis.sentiment.sentiment === "neutral"
                                  ? "#4b5563"
                                  : call.analysis.sentiment.sentiment === "satisfied"
                                  ? "#15803d"
                                  : "#166534",
                            }}
                          >
                            {call.analysis.sentiment.sentiment.toUpperCase()}
                          </MDTypography>

                          <MDBox>
                            <MDTypography variant="caption" fontSize={11} color="text">
                              Contact Sentiment
                            </MDTypography>
                          </MDBox>
                        </MDBox>

                        <Grid mt={1} container spacing={2}>
                          <Grid item xs={6}>
                            <MDBox
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              mb={0}
                            >
                              <MDBox position="relative" display="inline-flex">
                                <CircularProgress
                                  variant="determinate"
                                  value={100}
                                  size={100}
                                  thickness={3.6}
                                  sx={{
                                    color: "#e5e7eb",
                                    position: "absolute",
                                    left: 0,
                                  }}
                                />

                                <CircularProgress
                                  variant="determinate"
                                  value={call.analysis.scoring.score}
                                  size={100}
                                  thickness={3.6}
                                  sx={{
                                    color: "#163d7a",
                                  }}
                                />

                                <MDBox
                                  top={0}
                                  left={0}
                                  bottom={0}
                                  right={0}
                                  position="absolute"
                                  display="flex"
                                  flexDirection="column"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  <MDTypography variant="h3" fontWeight="bold" fontSize={20}>
                                    {call.analysis.scoring.score.toFixed(0)}%
                                  </MDTypography>

                                  <MDTypography variant="caption" color="text" fontSize={11}>
                                    Overall
                                  </MDTypography>
                                </MDBox>
                              </MDBox>
                            </MDBox>
                          </Grid>

                          {/* PROFESSIONALISM */}
                          <Grid item xs={6}>
                            <Card
                              sx={{
                                backgroundColor: "#ede9fe",
                                borderRadius: 3,
                                boxShadow: "none",
                                height: "100%",
                              }}
                            >
                              <MDBox
                                p={1}
                                textAlign="center"
                                display="flex"
                                flexDirection="column"
                                justifyContent="center"
                                alignItems="center"
                                height="100%"
                              >
                                <Icon
                                  sx={{
                                    color: "#7c3aed",
                                    mb: 1,
                                    fontSize: 22,
                                  }}
                                >
                                  workspace_premium
                                </Icon>

                                <MDTypography
                                  variant="button"
                                  fontWeight="bold"
                                  fontSize={14}
                                  sx={{
                                    color: "#7c3aed",
                                  }}
                                >
                                  Professionalism
                                </MDTypography>

                                <MDTypography variant="h4" fontWeight="bold" mt={0.5} fontSize={24}>
                                  {call.analysis.scoring.professionalism.toFixed(0)}
                                </MDTypography>
                              </MDBox>
                            </Card>
                          </Grid>

                          {[
                            {
                              label: "Control",
                              value: call.analysis.scoring.control,
                              color: "#e9f8ee",
                              text: "#16a34a",
                              icon: "support_agent",
                            },
                            {
                              label: "Clarity",
                              value: call.analysis.scoring.clarity,
                              color: "#e8f0ff",
                              text: "#2563eb",
                              icon: "record_voice_over",
                            },
                            {
                              label: "Empathy",
                              value: call.analysis.scoring.empathy,
                              color: "#fce7ef",
                              text: "#e11d48",
                              icon: "favorite",
                            },
                            {
                              label: "Listening",
                              value: call.analysis.scoring.listening,
                              color: "#fdf4d7",
                              text: "#d97706",
                              icon: "hearing",
                            },
                          ].map((item) => (
                            <Grid item xs={6} key={item.label}>
                              <Card
                                sx={{
                                  backgroundColor: item.color,
                                  borderRadius: 3,
                                  boxShadow: "none",
                                  height: "100%",
                                }}
                              >
                                <MDBox
                                  p={1}
                                  textAlign="center"
                                  display="flex"
                                  flexDirection="column"
                                  justifyContent="center"
                                  alignItems="center"
                                >
                                  <Icon
                                    sx={{
                                      color: item.text,
                                      mb: 0.5,
                                      fontSize: 22,
                                    }}
                                  >
                                    {item.icon}
                                  </Icon>

                                  <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    fontSize={13}
                                    sx={{
                                      color: item.text,
                                    }}
                                  >
                                    {item.label}
                                  </MDTypography>

                                  <MDTypography
                                    variant="h4"
                                    fontWeight="bold"
                                    mt={0.5}
                                    fontSize={24}
                                  >
                                    {item.value.toFixed(0)}
                                  </MDTypography>
                                </MDBox>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </MDBox>
                      <MDBox mt={5}>
                        <MDButton
                          fullWidth
                          color="dark"
                          onClick={() => setTranscript(call.analysis.transcript)}
                          endIcon={<Icon>forum</Icon>}
                        >
                          View Transcript
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  ) : (
                    <MDBox align="center">
                      <MDTypography fontSize={36}>
                        <Icon>info</Icon>
                      </MDTypography>
                      <MDTypography>
                        {call.recording ? "No call analysis available" : "Call was not recorded"}
                      </MDTypography>
                      {call.recording && (
                        <MDBox mt={5}>
                          <MDButton
                            color="dark"
                            onClick={() => generateAnalysis(call.reference)}
                            endIcon={<Icon>settings_suggestions</Icon>}
                          >
                            Generate Analysis
                          </MDButton>
                        </MDBox>
                      )}
                    </MDBox>
                  )}
                </Grid>
              </Grid>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton variant="outlined" color="dark" onClick={() => setCall(null)}>
              x close
            </MDButton>
          </DialogActions>
        </Dialog>
      )}

      <MDBox mt={1} mb={5}>
        <MDBox mb={1}>
          <Button
            color="dark"
            size="small"
            startIcon={<Icon>west</Icon>}
            onClick={() => {
              navigate(`/voice/call-centers/${ref}`);
            }}
          >
            Back
          </Button>
        </MDBox>
        {pageLoaded && (
          <MDBox>
            <Card sx={{ height: "100%", marginTop: "0px" }}>
              <MDBox p={0}>
                <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                  <MDBox>
                    <MDTypography variant="h6">Call Logs</MDTypography>
                  </MDBox>
                  <MDBox>
                    <Tooltip title="Refresh">
                      <MDButton
                        variant="outlined"
                        color="info"
                        iconOnly
                        onClick={() => {
                          setRefreshPage(!refreshPage);
                        }}
                      >
                        <Icon>replay</Icon>
                      </MDButton>
                    </Tooltip>
                  </MDBox>
                </MDBox>
                <MDBox mt={0}>
                  <DataTable
                    table={{ columns: tableColumns, rows: calls }}
                    isSorted
                    entriesPerPage
                    showTotalEntries
                    buttons
                    noEndBorder
                  />
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CallsPage;
